import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../core/models/base-state/base-state';
import { AccessLogActor } from '../../models/access-log-actor';
import { AccessLogEntry } from '../../models/access-log-entry';
import { AccessLogFilters, DEFAULT_ACCESS_LOG_FILTERS } from '../../models/access-log-filters';
import * as actions from '../actions/access-logs.actions';

export const accessLogsFeatureKey = 'access-logs';

/**
 * Rows per request. Larger than `DEFAULT_PAGE_SIZE` (10), which would fire three requests just to
 * fill a desktop viewport on first paint, and comfortably inside the server's enforced page-size
 * maximum — every row there costs several correlated subqueries, so this is not a dial to raise
 * casually.
 */
export const ACCESS_LOG_PAGE_SIZE = 25;

export interface AccessLogsState extends EntityState<AccessLogEntry> {
  baseState: baseState.BaseState;
  filters: AccessLogFilters;
  actors: AccessLogActor[];
  /**
   * True once the actor pick-list read has settled — succeeded OR failed — since the page was entered.
   *
   * "Settled at least once", not "in flight": the rows cannot be attributed until this read lands
   * (`selfActorIds` comes from it), so the list is held back while it is false, and a *retry* of the
   * pick list must not blank a list the patient is already reading. A failure sets it too, because at
   * that point the page has an honest answer to give — the notice beside the filter.
   */
  areActorsResolved: boolean;
  /**
   * Failures from the actor pick-list read, kept apart from `baseState.error` so a failed pick list
   * cannot make the activity list render its error state (and vice versa).
   */
  actorsError: unknown;
}

/**
 * No `sortComparer`: the server sorts by `CreatedOnUtc` descending and pages in that order, so
 * insertion order is already correct. A client-side comparer would also fight the server whenever a
 * new event lands between two page fetches.
 */
export const accessLogsAdapter = createEntityAdapter<AccessLogEntry>({
  selectId: entry => entry.patientPortalAccessLogId,
});

export const initialState: AccessLogsState = accessLogsAdapter.getInitialState({
  baseState: {
    ...baseState.createBaseState(undefined),
    pageSize: ACCESS_LOG_PAGE_SIZE,
  },
  filters: DEFAULT_ACCESS_LOG_FILTERS,
  actors: [],
  areActorsResolved: false,
  actorsError: null,
});

/**
 * Back to a first-page read: drop every loaded entry and reset paging.
 *
 * Shared by the initial load and by every filter change, because all filtering happens server-side —
 * a narrowed filter changes the whole result set, not just which loaded rows are visible, so keeping
 * the old entries would show stale rows above the newly fetched first page.
 */
function resetForReload(state: AccessLogsState): AccessLogsState {
  return {
    ...accessLogsAdapter.removeAll(state),
    baseState: {
      ...state.baseState,
      error: null,
      isLoading: true,
      pageNumber: 1,
      isAtEndOfData: false,
      totalRecordCount: 0,
    },
  };
}

export const accessLogsReducer = createReducer<AccessLogsState>(
  initialState,

  // Entering the page: a first-page read with the filters back at their defaults, and the actor pick
  // list emptied. Clearing `actors` matters on a patient switch — the previous patient's proxies must
  // not stay selectable in the "Accessed by" filter while the new patient's list loads.
  on(
    actions.initAccessLogs,
    (state): AccessLogsState => ({
      ...resetForReload(state),
      filters: DEFAULT_ACCESS_LOG_FILTERS,
      actors: [],
      areActorsResolved: false,
      actorsError: null,
    })
  ),

  on(actions.getAccessLogs, resetForReload),

  on(actions.setSearchTerm, (state, { searchTerm }): AccessLogsState => {
    const reset = resetForReload(state);
    return { ...reset, filters: { ...reset.filters, searchTerm } };
  }),

  on(actions.setDateRange, (state, { dateRange }): AccessLogsState => {
    const reset = resetForReload(state);
    return { ...reset, filters: { ...reset.filters, dateRange } };
  }),

  on(actions.setTypeFilter, (state, { typeFilter }): AccessLogsState => {
    const reset = resetForReload(state);
    return { ...reset, filters: { ...reset.filters, type: typeFilter } };
  }),

  on(actions.setActorFilter, (state, { actorId }): AccessLogsState => {
    const reset = resetForReload(state);
    return { ...reset, filters: { ...reset.filters, actorId } };
  }),

  // Resets the three dropdown filters and deliberately preserves `searchTerm`: the search field owns
  // its own clear affordance (the x inside it) and the Clear control is only shown for non-default
  // *filter* selections, so wiping the typed term from here would discard input the patient can still
  // see in the box.
  on(actions.clearFilters, (state): AccessLogsState => {
    const reset = resetForReload(state);
    return {
      ...reset,
      filters: { ...DEFAULT_ACCESS_LOG_FILTERS, searchTerm: reset.filters.searchTerm },
    };
  }),

  on(actions.nextPage, (state): AccessLogsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: (state.baseState.pageNumber ?? 0) + 1,
      },
    };
  }),

  // Retry of a failed page: clear the error and show loading, keeping pageNumber and every loaded
  // entry. Deliberately no removeAll and no pageNumber change — that is what separates it from
  // getAccessLogs above and nextPage before it.
  on(
    actions.retryPage,
    (state): AccessLogsState => ({
      ...state,
      baseState: { ...state.baseState, error: null, isLoading: true },
    })
  ),

  on(
    actions.getAccessLogsSuccess,
    (state, { entries, pageNumber, isAtEndOfData, totalRecordCount }): AccessLogsState => {
      return {
        ...accessLogsAdapter.addMany(entries, state),
        baseState: {
          ...state.baseState,
          error: null,
          isLoading: false,
          pageNumber,
          isAtEndOfData,
          totalRecordCount,
        },
      };
    }
  ),

  on(actions.getAccessLogsFailure, (state, { error }): AccessLogsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  // Deliberately does NOT clear `areActorsResolved`: this is also the notice's retry, and unresolving
  // it there would pull the loaded rows out from under the patient for the duration of the retry.
  on(actions.getAccessLogActors, (state): AccessLogsState => ({ ...state, actorsError: null })),

  on(
    actions.getAccessLogActorsSuccess,
    (state, { actors }): AccessLogsState => ({
      ...state,
      actors,
      areActorsResolved: true,
      actorsError: null,
    })
  ),

  on(
    actions.getAccessLogActorsFailure,
    (state, { error }): AccessLogsState => ({
      ...state,
      // The previously loaded list is deliberately left in place: a failed refresh should not empty a
      // pick list the patient may already have open.
      //
      // Resolved, not merely finished: a failure is an answer the page can render honestly (the
      // notice beside the filter), so the rows are released rather than held behind a spinner.
      areActorsResolved: true,
      actorsError: error,
    })
  )
);
